import {
  ArrowRight,
  Bookmark,
  Brain,
  CheckCircle2,
  Clock3,
  Cloud,
  Heart,
  LockKeyhole,
  MoveRight,
  SlidersHorizontal,
  Sparkles,
  Sprout,
  Zap,
} from "lucide-react";
import { externalLinkProps, installCta } from "../../lib/funnelLinks";

const recoveryMemory = [
  {
    title: "Last stuck moment",
    detail: "This morning, 9:42 AM",
    note: "Could not start work, felt overwhelmed",
    icon: Brain,
    tone: "bg-[#EDE9FE] text-[#7C3AED]",
  },
  {
    title: "What helped",
    detail: "Deep Unstick + Focus Session",
    note: "Breathing reset, then 25 min focus",
    icon: Heart,
    tone: "bg-[#DCFCE7] text-[#16A34A]",
  },
  {
    title: "Parked thoughts",
    detail: "3 items parked",
    note: "You can review when ready",
    icon: Bookmark,
    tone: "bg-[#FEF3C7] text-[#D97706]",
  },
  {
    title: "Resume thread",
    detail: "Project plan outline",
    note: "Last edited yesterday",
    icon: MoveRight,
    tone: "bg-[#DBEAFE] text-[#2563EB]",
  },
];

const proGroups = [
  {
    title: "Recover faster",
    copy: "Tools that help you get through it so you can move forward again.",
    icon: Zap,
    iconTone: "bg-[#DBEAFE] text-[#2563EB]",
    items: [
      "300 AI-assisted recoveries / month",
      "No daily AI throttling",
      "Focus Sessions",
      "Deep Unstick",
    ],
  },
  {
    title: "Remember progress",
    copy: "Your memory for what works, so you do not have to start over.",
    icon: Clock3,
    iconTone: "bg-[#EDE9FE] text-[#7C3AED]",
    items: [
      "Unlimited recovery history",
      "Guided thought review",
      "Remember & Review",
    ],
  },
  {
    title: "Reduce friction",
    copy: "More space, fewer limits, less resistance.",
    icon: Cloud,
    iconTone: "bg-[#DBEAFE] text-[#2563EB]",
    items: [
      "Unlimited parked thoughts",
      "No daily throttling",
      "One-tap routines",
    ],
  },
  {
    title: "Make it yours",
    copy: "Customize Mindrail around how your brain works.",
    icon: SlidersHorizontal,
    iconTone: "bg-[#EDE9FE] text-[#7C3AED]",
    items: [
      "Custom recovery style",
      "BYO API key",
      "Early access to new features",
    ],
  },
];

const ChromeIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="#F8FAFC" />
    <path d="M12 12h9.5A9.5 9.5 0 0 0 4.2 6.5L8.9 14.6A3.8 3.8 0 0 1 12 12Z" fill="#EF4444" />
    <path d="M12 12 7.25 20.2A9.5 9.5 0 0 0 21.5 12H12Z" fill="#FACC15" />
    <path d="M8.9 14.6 4.2 6.5A9.5 9.5 0 0 0 7.25 20.2L12 12a3.8 3.8 0 0 0-3.1 2.6Z" fill="#22C55E" />
    <circle cx="12" cy="12" r="4.05" fill="#2563EB" stroke="#F8FAFC" strokeWidth="1.25" />
  </svg>
);

function FeatureList({ items }) {
  return (
    <ul className="mt-2.5 grid gap-1.5 text-[12.5px] font-medium leading-[1.22] text-[#334155]">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <CheckCircle2
            className="mt-[1px] h-3.5 w-3.5 shrink-0 text-[#22A35D]"
            strokeWidth={2.15}
            aria-hidden="true"
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function RecoveryMemoryCard() {
  return (
    <article className="relative overflow-hidden rounded-[20px] border border-[#DCE6FA] bg-[oklch(99%_0.006_255)] px-5 py-4 shadow-[0_12px_34px_rgba(15,23,42,0.055)] sm:px-5">
      <div
        aria-hidden="true"
        className="absolute inset-x-12 bottom-24 h-24 rounded-full bg-[#DBEAFE]/70 blur-3xl"
      />
      <div className="relative flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#EDE9FE] text-[#7C3AED] shadow-[0_10px_24px_rgba(124,58,237,0.14)]">
          <Brain className="h-5 w-5" strokeWidth={1.9} aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-display text-[19px] font-semibold leading-tight text-[#1D4ED8] sm:text-[20px]">
            Your recovery memory
          </h3>
          <p className="mt-1 max-w-[300px] text-[13px] font-medium leading-[1.32] text-[#334155]">
            Mindrail connects the dots so you can pick up where it helps most.
          </p>
        </div>
      </div>

      <div className="relative mt-4 grid gap-2.5">
        <div
          aria-hidden="true"
          className="absolute left-[19px] top-5 h-[calc(100%-2.25rem)] w-[2px] bg-[#D8E4FF]"
        />
        {recoveryMemory.map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.title} className="relative grid grid-cols-[38px_minmax(0,1fr)] gap-3">
              <div className={`z-10 grid h-[38px] w-[38px] place-items-center rounded-full ${item.tone}`}>
                <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />
              </div>
              <div>
                <h4 className="text-[13px] font-bold leading-tight text-[#0F172A]">
                  {item.title}
                </h4>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-[#334155]">
                  {item.detail}
                </p>
                <p className="mt-0.5 text-[11.5px] font-medium leading-tight text-[#64748B]">
                  {item.note}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <svg
        viewBox="0 0 250 210"
        className="pointer-events-none absolute bottom-16 right-3 hidden h-[130px] w-[160px] text-[#8B9CF6] lg:block"
        aria-hidden="true"
      >
        <path
          d="M20 170c46-6 62-32 62-74 0-33 39-49 78-38 18 5 29-3 34-15"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="4"
          opacity=".72"
        />
        <circle cx="82" cy="96" r="5" fill="currentColor" opacity=".85" />
        <circle cx="160" cy="58" r="6" fill="currentColor" opacity=".85" />
        <circle cx="214" cy="24" r="13" fill="none" stroke="currentColor" strokeWidth="4" />
        <path
          d="M214 3v7M214 38v7M193 24h7M228 24h7M195 5l6 6M227 37l6 6M233 5l-6 6M201 37l-6 6"
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="4"
        />
      </svg>

      <div className="relative mt-4 flex items-center justify-center gap-2 rounded-xl bg-[#EFF4FF] px-4 py-2.5 text-center text-[12px] font-semibold leading-snug text-[#1D4ED8]">
        <Sparkles className="h-3.5 w-3.5 shrink-0" strokeWidth={1.9} aria-hidden="true" />
        Mindrail holds the thread so you do not have to.
      </div>
    </article>
  );
}

function ProOutcomeCard({ group }) {
  const Icon = group.icon;

  return (
    <article className="min-h-[128px] rounded-[18px] border border-[#DCE6FA] bg-[oklch(99%_0.006_255)] p-3.5 shadow-[0_10px_24px_rgba(15,23,42,0.035)]">
      <div className="flex items-start gap-3">
        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-full ${group.iconTone}`}>
          <Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h4 className="font-display text-[16px] font-semibold leading-tight text-[#0F2A63] sm:text-[17px]">
            {group.title}
          </h4>
          <p className="mt-1 max-w-[260px] text-[12px] font-medium leading-[1.3] text-[#475569]">
            {group.copy}
          </p>
        </div>
      </div>
      <FeatureList items={group.items} />
    </article>
  );
}

export default function ProPricing() {
  return (
    <section
      id="pricing"
      data-testid="pro-pricing-section"
      className="scroll-mt-28 bg-[#F7FAFF] px-4 py-10 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-[1120px]">
        <div
          data-testid="pro-pricing-shell"
          className="overflow-hidden rounded-[24px] border border-[#CADBFA] bg-[oklch(99%_0.006_255)] px-5 py-5 shadow-[0_16px_48px_rgba(37,99,235,0.06)] sm:px-6 lg:px-7"
        >
          <div className="mx-auto max-w-[860px] text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#EAF1FF] px-3 py-1.5 text-[13px] font-bold leading-none text-[#1D4ED8]">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
              Mindrail Pro
            </span>
            <h2 className="mx-auto mt-2.5 max-w-[860px] font-display text-[28px] font-semibold leading-[1.08] text-[#061943] sm:text-[34px] lg:text-[36px]">
              Keep your momentum, even after bad days.
            </h2>
            <p className="mx-auto mt-2.5 max-w-[680px] text-[14px] font-medium leading-[1.42] text-[#3B557A] sm:text-[15px]">
              Mindrail remembers what helped, what you parked, and where you left off without turning your browser into another task manager.
            </p>
          </div>

          <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,0.86fr)_minmax(0,1.14fr)] lg:items-center lg:gap-6">
            <RecoveryMemoryCard />

            <div>
              <h3 className="font-display text-[18px] font-semibold leading-tight text-[#071936] sm:text-[20px]">
                Pro helps you recover faster and stay consistent.
              </h3>
              <div
                data-testid="pricing-card-grid"
                className="mt-3 grid gap-3 md:grid-cols-2"
              >
                {proGroups.map((group) => (
                  <ProOutcomeCard key={group.title} group={group} />
                ))}
              </div>
            </div>
          </div>

          <div className="mt-3.5 rounded-[18px] border border-[#DCE6FA] bg-[#F0F4FF] px-4 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] sm:px-5">
            <div className="grid items-center gap-3.5 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.34fr)]">
              <div className="flex items-center gap-3.5">
                <div className="hidden h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#F8FAFF] text-[#7B8FEA] shadow-[0_10px_24px_rgba(37,99,235,0.08)] sm:grid">
                  <Sprout className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-display text-[18px] font-semibold leading-tight text-[#0F2A63] sm:text-[20px]">
                    Start free, unlock Pro when it helps.
                  </h3>
                  <p className="mt-1 text-[12.5px] font-medium leading-[1.35] text-[#334155]">
                    Pro starts inside Mindrail. Your data stays private by default.
                  </p>
                  <p className="mt-2 inline-flex rounded-full bg-[#EAF1FF] px-3 py-1 text-[12px] font-bold leading-none text-[#1D4ED8]">
                    Pro: $6.99/mo or $59/year
                  </p>
                </div>
              </div>

              <div>
                <a
                  href={installCta.href}
                  data-testid="pricing-install-cta"
                  className="inline-flex min-h-[44px] w-full items-center justify-center gap-2.5 rounded-xl bg-[#2563EB] px-4 text-[14px] font-bold leading-none text-white shadow-[0_10px_20px_rgba(37,99,235,0.2)] transition hover:bg-[#1D4ED8] focus:outline-none focus:ring-4 focus:ring-[#BFDBFE]"
                  {...externalLinkProps(installCta.external)}
                >
                  <ChromeIcon className="h-5 w-5" />
                  {installCta.label}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.05} aria-hidden="true" />
                </a>
                <p className="mt-2 flex items-center justify-center gap-2 text-center text-[11px] font-medium leading-snug text-[#6478B8]">
                  <LockKeyhole className="h-3.5 w-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
                  Private by default. Your data stays on your device.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
